import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { AuthCard } from '../components/landing/AuthCard';
import { LandingSections } from '../components/landing/LandingSections';

export default function Landing() {
  usePageTitle(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState(null);
  const howRef = useRef(null);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const scrollToHow = () => howRef.current?.scrollIntoView({ behavior: 'smooth' });

  const openRegister = () => {
    setAuthMode('register');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#080b11] text-gray-100" data-testid="landing-page">

      {/* ═══════ NAV ═══════ */}
      <nav className="sticky top-0 z-50 bg-[#080b11]/80 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-1 font-black text-base tracking-tight shrink-0">
            <span className="text-blue-500">QCM</span><span>AntiTriche</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="nav-login-btn" variant="ghost" onClick={() => { setAuthMode(authMode === 'login' ? null : 'login'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-sm text-gray-400 hover:text-white px-2 sm:px-4">Connexion</Button>
            <Button data-testid="nav-register-btn" onClick={openRegister} className="bg-blue-600 text-white hover:bg-blue-700 text-xs sm:text-sm h-9 px-3 sm:px-5 whitespace-nowrap">Commencer gratuitement</Button>
          </div>
        </div>
      </nav>

      {/* ═══════ 1. HERO ═══════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_hsl(217_91%_55%/0.10),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-24 pb-10 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-6 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black leading-[1.15] tracking-tight mb-6">
                Créez, imprimez et corrigez vos QCM papier en quelques minutes
              </h1>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-5">
                À partir de vos propres supports de cours, QCMAntiTriche génère en quelques secondes des questionnaires adaptés à votre classe et à votre niveau de difficulté, puis corrige automatiquement les copies après un simple scan.
              </p>
              <p className="text-sm sm:text-base font-semibold text-blue-400 mb-8">
                Moins de préparation. Moins de correction. Plus de temps pour enseigner.
              </p>

              {!authMode && (
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Button data-testid="hero-register-btn" onClick={openRegister} size="lg" className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/30 text-base px-8 h-12">
                    Commencer gratuitement <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button data-testid="hero-how-btn" onClick={scrollToHow} size="lg" variant="ghost" className="text-base text-gray-400 hover:text-white border border-white/[0.06] h-12">
                    <ChevronDown className="w-4 h-4 mr-2" /> Voir comment ça fonctionne
                  </Button>
                </div>
              )}
              <p className="text-xs text-gray-500">Pensé pour les enseignants du collège, du lycée et du post-bac.</p>
            </div>

            {/* Right: Auth form OR hero image */}
            {authMode ? (
              <div className="flex justify-center lg:justify-end">
                <AuthCard authMode={authMode} setAuthMode={setAuthMode} />
              </div>
            ) : (
              <div className="flex justify-center lg:justify-end">
                <img
                  src="https://customer-assets.emergentagent.com/job_antitriche-hub/artifacts/40jxm0xm_Capture%20d%E2%80%99e%CC%81cran%202026-03-26%20a%CC%80%2013.25.08.png"
                  alt="Aperçu de la plateforme QCMAntiTriche"
                  className="w-full rounded-2xl border border-white/[0.06] shadow-2xl shadow-black/50"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <LandingSections howRef={howRef} openRegister={openRegister} />
    </div>
  );
}
